import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Box, Typography } from '@mui/material'

interface TooltipItem {
  id: string
  name: string
  icon?: React.ReactNode
  color?: string
  image?: string
}

interface AnimatedTooltipProps {
  items: TooltipItem[]
  onSelect?: (item: TooltipItem) => void
  selectedId?: string
}

export default function AnimatedTooltip({
  items,
  onSelect,
  selectedId,
}: AnimatedTooltipProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const handleMouseEnter = (index: number) => {
    setActiveIndex(index)
  }

  const handleMouseLeave = () => {
    setActiveIndex(null)
  }

  const handleClick = (item: TooltipItem) => {
    if (onSelect) {
      onSelect(item)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        justifyContent: { xs: 'center', lg: 'flex-start' },
      }}
    >
      {items.map((item, index) => (
        <Box
          key={item.id}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(item)}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'transform 0.3s',
            overflow: 'hidden',
            transform: selectedId === item.id ? 'scale(1.1)' : undefined,
            boxShadow:
              selectedId === item.id
                ? '0 0 0 2px #b45309, 0 0 0 6px #fff'
                : undefined,
            '&:hover': { transform: 'scale(1.1)' },
          }}
        >
          {item.image ? (
            <Box
              component="img"
              src={item.image}
              alt={item.name}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
            />
          ) : item.icon ? (
            item.icon
          ) : (
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              {item.name.charAt(0).toUpperCase()}
            </Typography>
          )}

          <AnimatePresence>
            {activeIndex === index && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                style={{ position: 'absolute', zIndex: 50, pointerEvents: 'none', left: '50%', top: '-60px', transform: 'translateX(-50%)' }}
              >
                <Box
                  sx={{
                    bgcolor: '#111827',
                    color: '#fff',
                    fontSize: 12,
                    borderRadius: 1,
                    py: 1,
                    px: 1.5,
                    boxShadow: 3,
                    whiteSpace: 'nowrap',
                    position: 'relative',
                  }}
                >
                  {item.name}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      bottom: 0,
                      transform: 'translateX(-50%) translateY(100%)',
                      width: 8,
                      height: 8,
                      bgcolor: '#111827',
                      rotate: '45deg',
                    }}
                  />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      ))}
    </Box>
  )
}

